import { useCallback, useEffect, useRef, useState } from 'react';

import { flip, offset, shift, useFloating } from '@floating-ui/react-dom';
import { twMerge } from 'tailwind-merge';
import { Tooltip } from 'ui';
import { IconButton } from 'ui/atoms';
import useClickAway from 'ui/src/hooks/useClickAway';

const AdditionalButton = ({
  icon,
  shorcuts,
  additional,
}: {
  icon: JSX.Element;
  shorcuts: (onClose: () => void) => JSX.Element;
  additional?: JSX.Element;
}) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);

  const { refs, floatingStyles, update } = useFloating({
    placement: 'top',
    middleware: [offset(4), flip(), shift()],
  });

  const threeDotsRef = useRef<HTMLDivElement | null>(null);

  useClickAway(threeDotsRef, () => {
    if (isOpen) {
      setIsOpen(false);
    }
  });

  const toggleOpen = useCallback(() => {
    setIsOpen((prevState) => !prevState);
  }, []);

  useEffect(() => {
    if (!isOpen) return;

    const handleResize = () => {
      update(); // Recalculate floating position
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isOpen, update]);

  return (
    <div
      ref={(currentRef) => {
        threeDotsRef.current = currentRef;
        refs.setReference(currentRef);
      }}
    >
      <Tooltip title={isOpen ? '' : 'Additional'} placement="top">
        <div className="relative">
          <IconButton
            open={isOpen}
            className={twMerge(
              'flex items-center justify-center w-9 h-9 hover:bg-transparent active:bg-v2blueGray-100',
              isOpen && 'bg-v2blueGray-100 hover:bg-v2blueGray-100',
            )}
            onClick={toggleOpen}
          >
            {icon}
          </IconButton>
        </div>
      </Tooltip>

      {isOpen && (
        <div
          ref={refs.setFloating}
          style={{ ...floatingStyles, position: 'fixed' }}
          className="flex p-4 rounded-[22px] bg-white shadow-generic-popup"
        >
          {shorcuts(() => setIsOpen(false))}
          {additional}
        </div>
      )}
    </div>
  );
};

export { AdditionalButton };
